/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../../App";
import { isFleetDomain } from "../../config/productConfig";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Mail, Plus, Trash2, Send, Eye, AlertCircle, CheckCircle2, Clock, FileText, Download,
  Users,
} from "lucide-react";


export default function EmailPanel() {
  const { school } = useAuth();
  // Incentive e-mails are about training students; the fleet product has none.
  const hideTrainingCards = isFleetDomain() || school?.product === "fleet";
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSending, setReportSending] = useState(false);

  // Reminders state
  const [reminderLog, setReminderLog] = useState([]);
  const [reminderSending, setReminderSending] = useState(null);
  const [reminderPreview, setReminderPreview] = useState(null);   // { type, would_send, capped, recipients }

  // Simulação: só diz quem receberia, não envia nada
  const fetchReminderRecipients = async (type) => {
    const r = await axios.post(`${API}/admin/reminders/send-now?type=${type}&dry_run=true`, {}, { withCredentials: true });
    return r.data;
  };

  const previewReminder = async (type) => {
    setReminderSending(type);
    try {
      const d = await fetchReminderRecipients(type);
      setReminderPreview({ type, would_send: d.would_send || 0, capped: d.capped || 0, recipients: d.recipients || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao ver os destinatários");
    } finally {
      setReminderSending(null);
    }
  };

  const REMINDER_NAMES = { no_sub: "Sem subscrição", inactive: "Alunos inativos" };

  // O envio mostra primeiro a quem vai e pede confirmação (um lembrete enviado não se desfaz)
  const sendReminder = async (type) => {
    setReminderSending(type);
    try {
      const dry = await fetchReminderRecipients(type);
      const n = dry.would_send || 0;
      if (n === 0) {
        toast.message("Ninguém para avisar: todos cumprem o critério.");
        return;
      }
      const list = (dry.recipients || []).slice(0, 15).map((x) => `• ${x.name || "—"} (${x.email})`).join("\n");
      const more = n > 15 ? `\n… e mais ${n - 15}` : "";
      const capNote = dry.capped ? `\n\n(${dry.capped} pessoa(s) já receberam 3 lembretes e ficam de fora.)` : "";
      if (!window.confirm(`Lembrete «${REMINDER_NAMES[type]}»: vai ser enviado a ${n} pessoa(s):\n\n${list}${more}${capNote}\n\nEnviar agora?`)) return;
      const r = await axios.post(`${API}/admin/reminders/send-now?type=${type}`, {}, { withCredentials: true });
      const d = r.data;
      toast.success(`Lembretes enviados: ${d.sent} enviados · ${d.skipped} ignorados`);
      setReminderPreview(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar lembretes");
    } finally {
      setReminderSending(null);
    }
  };

  const reload = async () => {
    try {
      const [p, l, rl] = await Promise.all([
        axios.get(`${API}/di/email/prefs`, { withCredentials: true }).then((r) => r.data),
        axios.get(`${API}/di/email/log?limit=20`, { withCredentials: true }).then((r) => r.data).catch(() => []),
        axios.get(`${API}/admin/reminders/log?limit=10`, { withCredentials: true }).then((r) => r.data).catch(() => []),
      ]);
      setPrefs(p);
      setLog(l);
      setReminderLog(rl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar preferências");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const savePrefs = async (update) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/di/email/prefs`, update, { withCredentials: true });
      setPrefs(r.data);
      toast.success("Preferências guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = async () => {
    const email = newRecipient.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Email inválido");
      return;
    }
    const next = [...(prefs?.extra_recipients || []), email];
    await savePrefs({ extra_recipients: next });
    setNewRecipient("");
  };

  const removeRecipient = async (email) => {
    const next = (prefs?.extra_recipients || []).filter((e) => e !== email);
    await savePrefs({ extra_recipients: next });
  };

  const onPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await axios.get(`${API}/di/email/preview`, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setPreviewLoading(false);
    }
  };

  const onSendNow = async () => {
    if (!window.confirm("Enviar email de resumo agora para todos os destinatários?")) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/di/email/send-now`, {}, { withCredentials: true });
      if (r.data?.skipped) toast.message(`Nada para enviar — ${r.data.skipped}`);
      else toast.success(`Enviado para ${r.data.sent || 0} destinatário(s) · ${r.data.total_items || 0} item(s)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setSending(false);
    }
  };

  const onReportPreview = async () => {
    setReportLoading(true);
    try {
      const r = await axios.get(`${API}/di/reports/monthly`, { withCredentials: true });
      setReportSummary(r.data);
      toast.success(`Relatório de ${r.data.label} pronto · ${(r.data.size_bytes / 1024).toFixed(1)} KB`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar relatório");
    } finally {
      setReportLoading(false);
    }
  };

  const onReportDownload = async () => {
    setReportLoading(true);
    try {
      const r = await axios.get(`${API}/di/reports/monthly?download=1`, {
        withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `di_relatorio_${(reportSummary?.month) || "mensal"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erro ao descarregar PDF");
    } finally {
      setReportLoading(false);
    }
  };

  const onReportSend = async () => {
    if (!window.confirm("Enviar relatório mensal por email agora?")) return;
    setReportSending(true);
    try {
      const r = await axios.post(`${API}/di/reports/monthly/send`, {}, { withCredentials: true });
      if (r.data?.skipped) toast.message(`Nada para enviar — ${r.data.skipped}`);
      else toast.success(`Relatório enviado para ${r.data.sent || 0} destinatário(s) · ${r.data.month}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao enviar relatório");
    } finally {
      setReportSending(false);
    }
  };

  if (loading) return <div className="text-slate-500 text-sm p-6">A carregar…</div>;

  return (
    <div className="space-y-4" data-testid="di-email-panel">
      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-blue-300">
            <Mail className="w-5 h-5" /> Notificações por email
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400">
          Email diário automático às <strong className="text-amber-300">08:00 (Europe/Lisbon)</strong> com tudo o que está crítico/atenção nos próximos 30 dias.
          {!prefs.configured && (
            <div className="mt-2 flex items-center gap-2 px-2 py-1.5 rounded border border-red-500/40 bg-red-500/10 text-red-300 text-[11px]" data-testid="di-email-not-configured">
              <AlertCircle className="w-4 h-4" /> Serviço Resend não configurado no servidor (RESEND_API_KEY em falta).
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Preferências da escola</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-slate-100 text-sm">Notificações ativas</div>
              <div className="text-[11px] text-slate-500">Desligar suspende o envio diário; envios manuais continuam disponíveis.</div>
            </div>
            <Switch
              checked={prefs.enabled !== false}
              onCheckedChange={(v) => savePrefs({ enabled: v })}
              disabled={saving}
              data-testid="di-email-enabled-toggle"
            />
          </div>

          <div>
            <div className="text-slate-100 text-sm mb-1">Destinatários extra</div>
            <div className="text-[11px] text-slate-500 mb-2">
              Os admins da escola recebem automaticamente. Aqui podes adicionar outros emails (mecânico, diretor de operações, etc).
            </div>
            <div className="space-y-1 mb-2">
              {(prefs.extra_recipients || []).length === 0 ? (
                <div className="text-[11px] text-slate-500 italic">Nenhum destinatário extra.</div>
              ) : (
                (prefs.extra_recipients || []).map((e) => (
                  <div key={e} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-slate-700 bg-slate-900/40 text-xs" data-testid="di-email-recipient-row">
                    <span className="text-slate-200 truncate">{e}</span>
                    <button
                      onClick={() => removeRecipient(e)}
                      className="p-1 text-slate-400 hover:text-red-400"
                      data-testid={`di-email-recipient-remove-${e}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="adicionar@exemplo.com"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                className="bg-slate-800 border-slate-700 h-9"
                onKeyDown={(e) => { if (e.key === "Enter") addRecipient(); }}
                data-testid="di-email-recipient-input"
              />
              <Button size="sm" onClick={addRecipient} disabled={saving} className="btn-cockpit" data-testid="di-email-recipient-add">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            <span>Ações</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onPreview} disabled={previewLoading} className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10" data-testid="di-email-preview-btn">
              <Eye className="w-4 h-4 mr-1" /> Pré-visualizar conteúdo
            </Button>
            <Button size="sm" onClick={onSendNow} disabled={sending || !prefs.configured} className="bg-blue-600 hover:bg-blue-500" data-testid="di-email-send-now-btn">
              <Send className="w-4 h-4 mr-1" /> {sending ? "A enviar…" : "Enviar resumo agora"}
            </Button>
          </div>

          {preview && (
            <div className="mt-3 p-3 rounded border border-slate-700 bg-slate-950/40" data-testid="di-email-preview">
              {preview.empty ? (
                <div className="text-emerald-300 text-xs flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Sem itens críticos nos próximos 30 dias — nada seria enviado agora.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-slate-300 text-xs font-semibold">{preview.totals.total} alerta(s) seriam enviados:</div>
                  <ul className="text-[11px] text-slate-400 space-y-0.5 list-disc pl-5">
                    <li>{preview.totals.aircraft_docs} validades de aeronave</li>
                    <li>{preview.totals.insurance} seguros</li>
                    <li>{preview.totals.maintenance} manutenções</li>
                    <li>{preview.totals.instructors} validades de instrutores</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-violet-300">
            <FileText className="w-4 h-4" /> Relatório Mensal (PDF)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          <div className="text-[11px] text-slate-500">
            Snapshot mensal em PDF com horas voadas, instrutores, seguros e alertas críticos.
            Enviado automaticamente no dia <strong className="text-amber-300">1 de cada mês às 08:05 (Europe/Lisbon)</strong>.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onReportPreview} disabled={reportLoading}
              className="border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
              data-testid="di-report-preview-btn">
              <Eye className="w-4 h-4 mr-1" /> Pré-visualizar
            </Button>
            <Button size="sm" variant="outline" onClick={onReportDownload}
              disabled={reportLoading || !reportSummary}
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="di-report-download-btn">
              <Download className="w-4 h-4 mr-1" /> Descarregar PDF
            </Button>
            <Button size="sm" onClick={onReportSend} disabled={reportSending || !prefs.configured}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="di-report-send-btn">
              <Send className="w-4 h-4 mr-1" /> {reportSending ? "A enviar…" : "Enviar agora"}
            </Button>
          </div>
          {reportSummary && (
            <div className="mt-3 p-3 rounded border border-slate-700 bg-slate-950/40 space-y-1"
              data-testid="di-report-summary">
              <div className="text-slate-200 text-xs font-semibold">{reportSummary.label}</div>
              <ul className="text-[11px] text-slate-400 list-disc pl-5 space-y-0.5">
                <li>{reportSummary.totals.aircraft} aeronave(s) · {reportSummary.totals.instructors} instrutor(es)</li>
                <li>{reportSummary.totals.flights_in_month} voos no mês ({Math.round(reportSummary.totals.month_minutes / 60)}h)</li>
                <li>{reportSummary.totals.critical_alerts} alertas críticos · {reportSummary.totals.insurance_expiring_60d} seguros ≤60d</li>
              </ul>
              <div className="text-[10px] text-slate-500">PDF: {(reportSummary.size_bytes / 1024).toFixed(1)} KB</div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="w-4 h-4" /> Histórico recente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <div className="text-[11px] text-slate-500 italic">Sem envios registados.</div>
          ) : (
            <div className="space-y-1" data-testid="di-email-log">
              {log.map((row, i) => (
                <div key={i} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-slate-800 bg-slate-900/40 text-[11px]">
                  <span className="font-mono text-slate-500">{(row.sent_at || "").slice(0, 16)}</span>
                  <span className="text-slate-200 truncate flex-1">{row.to}</span>
                  <Badge variant="outline" className="text-[9px] border-slate-700">{row.total_items} itens</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── E-mails de incentivo ────────────────────────── */}
      {!hideTrainingCards && (
      <Card className="instrument-panel" data-testid="di-reminders-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" /> E-mails de Incentivo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-[11px] text-slate-400 leading-relaxed border border-blue-500/20 bg-blue-500/5 rounded p-2.5">
            <div className="font-semibold text-blue-300 mb-1">Quando são enviados automaticamente?</div>
            <div>• <b>Sem subscrição</b> (só alunos cujo período de teste acabou ou que nunca subscreveram; instrutores e administradores nunca; no máximo 3 lembretes por pessoa) → dias <b>1 e 15</b> de cada mês às 09:00</div>
            <div>• <b>Alunos inativos</b> (alunos com subscrição que já treinaram, mas sem treino nem exame há 14+ dias) → dia <b>8</b> de cada mês às 09:00</div>
            <div className="mt-1 text-[10px] text-slate-500">Cópia enviada para omigueis@gmail.com · Utilizadores podem cancelar via link no rodapé</div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              disabled={reminderSending === "no_sub"}
              onClick={() => sendReminder("no_sub")}
              data-testid="di-reminder-nosub-btn"
              className="text-[11px] border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              {reminderSending === "no_sub" ? "A enviar…" : "Enviar agora — Sem Subscrição"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={reminderSending === "inactive"}
              onClick={() => sendReminder("inactive")}
              data-testid="di-reminder-inactive-btn"
              className="text-[11px] border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
            >
              <Send className="w-3.5 h-3.5 mr-1" />
              {reminderSending === "inactive" ? "A enviar…" : "Enviar agora — Alunos Inativos"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!!reminderSending}
              onClick={() => previewReminder("inactive")}
              data-testid="di-reminder-inactive-preview-btn"
              className="text-[11px] border-slate-600 text-slate-300 hover:bg-slate-700/40"
            >
              Ver quem receberia — Alunos Inativos
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!!reminderSending}
              onClick={() => previewReminder("no_sub")}
              data-testid="di-reminder-nosub-preview-btn"
              className="text-[11px] border-slate-600 text-slate-300 hover:bg-slate-700/40"
            >
              Ver quem receberia — Sem Subscrição
            </Button>
          </div>

          {reminderPreview && (
            <div className="text-[11px] border border-slate-700 bg-slate-900/50 rounded p-2.5 space-y-1" data-testid="di-reminder-preview">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-200">
                  «{REMINDER_NAMES[reminderPreview.type]}»: receberiam o lembrete {reminderPreview.would_send} pessoa(s) (nada foi enviado){reminderPreview.capped ? ` · ${reminderPreview.capped} já receberam 3 lembretes e ficam de fora` : ""}
                </span>
                <button type="button" onClick={() => setReminderPreview(null)} className="text-slate-500 hover:text-white" aria-label="Fechar">✕</button>
              </div>
              {reminderPreview.recipients.length === 0 ? (
                <div className="text-emerald-300/80 italic">Ninguém: todos cumprem o critério.</div>
              ) : (
                <ul className="max-h-40 overflow-y-auto space-y-0.5 text-slate-300">
                  {reminderPreview.recipients.map((x) => <li key={x.email}>{x.name || "—"} <span className="text-slate-500">({x.email})</span></li>)}
                </ul>
              )}
            </div>
          )}

          {reminderLog.length > 0 && (
            <div className="space-y-1 pt-1 border-t border-slate-800">
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Histórico de lembretes</div>
              {reminderLog.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-[10px] px-2 py-1 rounded bg-slate-900/40 border border-slate-800">
                  <span className="font-mono text-slate-500">{(r.sent_at || "").slice(0, 16)}</span>
                  <span className="text-slate-400">{r.type === "no_sub" ? "Sem subscrição" : "Inativos"}</span>
                  <span className="text-emerald-400">{r.sent} enviados</span>
                  <span className="text-slate-600">{r.skipped} ignorados</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
